import React, { useState, useMemo } from 'react'
import Chip from '@mui/material/Chip'
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    Grid,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Divider,
    Button,
    Snackbar,
    CircularProgress,
} from '@material-ui/core'
import { Stack, Alert } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import InfoIcon from '@mui/icons-material/Info'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { Auth } from 'aws-amplify'
import { logger } from '../../../../config/logger'

import IMSFormAccordion from './Accordion/IMSFormAccordion'
import IMSContentLoader from './IMSContentLoader'
import LoadFormPreview from '../LoadFormPreview'
import LightboxDialog from '../../components/LightboxDialog'
import { isAdmin, isMultipleOrganization } from '../../../../Utils/ServerUtil'
import {
    saveIMSForms,
    submitItem,
    savePopulatedForms,
} from '../../../../Utils/SubmitUtil'
import { AutoPopulatedComponentCheck, FieldValues } from './IMSFormFieldsTypes'
import TextField from '@mui/material/TextField'
import config from '../../../../config/runtime'

type ImsFormFieldsProps = {
    handleSaveSuccess: () => void
    prevEvalItem: any
    handleIMSFormSave: () => Promise<any>
    setSaveForm: (value: boolean) => void
    setImsForm: (data: any) => void
    loadForm: boolean
    selectedCategory: any
    successAlert: boolean
    handleSaveSuccessClose: () => void
    imsForm: any
    error: string | null
    loading: boolean
    ims_forms_data: any
    titleStyle?: React.CSSProperties
    internaUseValues: React.CSSProperties
    internaUseValues2: React.CSSProperties
    evaluation: any
    nominationValue: string | number
    handleNominationChange: (
        event: React.ChangeEvent<{ value: unknown }>
    ) => void
    setFieldValues: (values: FieldValues) => void
    fieldValues: FieldValues
    handleFieldChange: (
        groupId: string | number,
        componentId: string | number,
        value: any,
        datatype: string
    ) => void
    newIMSData: any
}

const ImsFormFields = (props: ImsFormFieldsProps) => {
    const {
        loadForm,
        selectedCategory,
        successAlert,
        handleSaveSuccessClose,
        imsForm,
        error,
        loading,
        ims_forms_data,
        titleStyle,
        internaUseValues,
        internaUseValues2,
        evaluation,
        nominationValue,
        handleNominationChange,
        fieldValues,
        setFieldValues,
        handleFieldChange,
        newIMSData,
        handleIMSFormSave,
        setSaveForm,
        setImsForm,
        prevEvalItem,
        handleSaveSuccess,
    } = props

    const [lightboxImage, setLightboxImage] = useState<string | null>(null)
    const [lightboxOpen, setLightboxOpen] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [isPopulating, setIsPopulating] = useState<boolean>(false)
    const [populatedSuccess, setPopulatedSuccess] = useState<boolean>(false)
    const [populateError, setPopulateError] = useState<boolean>(false)
    const [formNotSavedAlert, setFormNotSavedAlert] = useState<boolean>(false)
    const [vinInput, setVinInput] = useState<string>('')
    const [overwriteDialogOpen, setOverwriteDialogOpen] = useState<boolean>(false)

    const populateFormAPI = async (itemId: string, vinNumber?: string) => {
        const url = `${config.API_ENDPOINT}/items/${itemId}/populate_form`
        let response: Response | undefined

        try {
            let jwtToken
            let tokenRefreshError = false

            try {
                const session = await Auth.currentSession()
                jwtToken = session.getIdToken().getJwtToken()
            } catch (e) {
                try {
                    const session = await Auth.currentSession()
                    jwtToken = session.getIdToken().getJwtToken()
                } catch (e) {
                    tokenRefreshError = true
                }
            }

            const headers: Record<string, string> = {
                authorization: jwtToken || '',
                accept: 'application/json',
                'Content-Type': 'application/json',
            }

            if (isAdmin() || isMultipleOrganization()) {
                const organization =
                    localStorage['hw.' + window.btoa('organization')]
                if (organization && window.atob(organization)) {
                    headers['HW-Organization'] = window.atob(organization)
                }
            }

            const filteredHeaders = Object.fromEntries(
                Object.entries(headers).filter(
                    ([_, value]) => value !== undefined
                )
            )

            const options: RequestInit = {
                method: 'POST',
                headers: filteredHeaders,
                body: JSON.stringify(vinNumber ? { vin: vinNumber } : {}),
            }

            logger.info('ims_populate_fetch_start', {
                http: { method: 'POST', url },
                item_id: itemId,
                vin: vinNumber,
            })

            response = await fetch(url, options)

            if (!response.ok) {
                const errorBody = await response.text() // capture backend error
                const error = new Error('Populate form request failed')

                    // attach custom fields
                    ; (error as any).status = response.status
                    ; (error as any).url = url
                    ; (error as any).responseBody = errorBody

                throw error
            }

            const data = await response.json()

            logger.info('ims_populate_fetch_success', {
                http: {
                    method: 'POST',
                    url,
                    status: response.status || 'NETWORK_ERROR',
                },
                item_id: itemId,
                vin: vinNumber,
                evalID: imsForm?.item?.eval_id,
            })

            return data
        } catch (error: any) {
            logger.error('ims_populate_fetch_failure', {
                http: {
                    method: 'POST',
                    url,
                    status:
                        error?.status || response?.status || 'NETWORK_ERROR',
                },
                item_id: itemId,
                vin: vinNumber,
                evalID: imsForm?.item?.eval_id,
                message: error?.message,
                response_body: error?.responseBody,
            })

            setPopulateError(true)
            throw error
        }
    }

    const performPopulate = async (vin: string) => {
        try {
            setIsPopulating(true)
            const populateResponse = await populateFormAPI(
                imsForm.item.item_id,
                vin
            )
            if (populateResponse) {
                const newFieldValues: FieldValues = {}
                setPopulatedSuccess(true)

                populateResponse.groups?.forEach((group: any) => {
                    group.components?.forEach((component: any) => {
                        // Manually populate VIN field if it's empty but VIN is available
                        if (
                            (component.label?.toLowerCase() === 'vin' ||
                                component.label?.toLowerCase() ===
                                    'vehicle identification number') &&
                            !component.component_response
                        ) {
                            component.component_response = vin
                        }

                        newFieldValues[String(component.id)] = {
                            value: component.component_response,
                            is_auto_populated: true,
                        }
                    })
                })

                const updatedFormData = {
                    ...imsForm,
                    item: {
                        ...imsForm.item,
                        ims_forms_data: { ...populateResponse },
                    },
                }

                setFieldValues(newFieldValues)
                setImsForm(updatedFormData)

                await savePopulatedForms(
                    imsForm.item.item_id,
                    updatedFormData
                )
                handleSaveSuccess()
            }
        } catch (error) {
            setSaveForm(false)
        } finally {
            setIsPopulating(false)
        }
    }

    const handlePopulateFormToIMS = async () => {
        if (!imsForm?.item?.ims_forms_data?.groups) {
            setFormNotSavedAlert(true)
            return
        }

        // Check if form has data
        const hasData = Object.keys(fieldValues).some((key) => {
            const val = fieldValues[key]
            if (typeof val === 'object' && val !== null) {
                return val.value !== '' && val.value !== null && val.value !== false && val.value !== undefined
            }
            return val !== '' && val !== null && val !== false && val !== undefined
        })

        if (hasData) {
            setOverwriteDialogOpen(true)
            return
        }

        const vin = imsForm?.item?.vin
        if (!vin || (typeof vin === 'string' && !vin.trim())) {
            setIsModalOpen(true)
        } else {
            await performPopulate(vin)
        }
    }

    const handleConfirmOverwrite = async () => {
        setOverwriteDialogOpen(false)
        const vin = imsForm?.item?.vin
        if (!vin || (typeof vin === 'string' && !vin.trim())) {
            setIsModalOpen(true)
        } else {
            await performPopulate(vin)
        }
    }

    const handleModalSubmit = async () => {
        try {
            if (!vinInput.trim()) {
                alert('Please enter a VIN number')
                return
            }

            setIsPopulating(true)
            const populateResponse = await populateFormAPI(
                imsForm.item.item_id,
                vinInput
            )

            if (populateResponse) {
                const saveItemWithVIN = { ...imsForm.item, vin: vinInput }

                await submitItem(
                    imsForm.item.item_id,
                    saveItemWithVIN,
                    imsForm.item.owner,
                    imsForm.item.owner_name,
                    imsForm.item,
                    null
                )

                setIsModalOpen(false)
                setVinInput('')
                setPopulatedSuccess(true)
                const newFieldValues: FieldValues = {}

                populateResponse.groups?.forEach((group: any) => {
                    group.components?.forEach((component: any) => {
                        const isPhotoCapture = component.photo_capture === 1
                        const componentResponse = component.component_response
                        const normalizedValue =
                            isPhotoCapture &&
                            componentResponse &&
                            typeof componentResponse === 'object' &&
                            'value' in componentResponse
                                ? componentResponse.value
                                : componentResponse
                        const normalizedPhoto =
                            isPhotoCapture &&
                            componentResponse &&
                            typeof componentResponse === 'object' &&
                            'photo' in componentResponse
                                ? componentResponse.photo
                                : ''

                        newFieldValues[String(component.id)] = {
                            value: normalizedValue,
                            photo: isPhotoCapture ? normalizedPhoto : undefined,
                            is_auto_populated: true,
                        }
                    })
                })

                const updatedFormData = {
                    ...imsForm,
                    item: {
                        ...imsForm.item,
                        ims_forms_data: { ...populateResponse },
                    },
                }

                setFieldValues(newFieldValues)
                setImsForm(updatedFormData)

                await savePopulatedForms(imsForm.item.item_id, updatedFormData)
                setSaveForm(true)
            }
        } catch (error) {
            setSaveForm(false)
        } finally {
            setIsPopulating(false)
        }
    }

    const shouldShowAutoPopulatedIndicator = (
        component: AutoPopulatedComponentCheck | any,
        currentFieldValues: FieldValues
    ): boolean => {
        if (component.id === null || component.id === undefined) {
            return false
        }

        if (!component.is_auto_populated) return false

        // Handle Checkbox Group (identified by having 'values' array)
        if (component.values && Array.isArray(component.values)) {
            // Check if *any* of the options is currently checked AND matches the populated state.
            // If the populated state for ALL options is false, then no dot should show?
            // Wait, if VIN populates a checkbox group, at least one option must be true, right?
            // If not, then is_auto_populated shouldn't be true.

            // The user says "except the checkbox group field which was also populated".
            // This means at least one checkbox was checked by VIN.

            // We check if the current state of ALL checkboxes matches the populated state.
            return component.values.every((option: any) => {
                const optionId = String(option.id)
                const fieldValueData = currentFieldValues[optionId]

                // Normalize current value to boolean
                // Check for object format { value: ... } or direct value
                const currentVal =
                    typeof fieldValueData === 'object' &&
                        fieldValueData !== null &&
                        'value' in fieldValueData
                        ? fieldValueData.value
                        : fieldValueData

                const currentBool =
                    currentVal === true || currentVal === 'true'

                // Normalize populated value
                const populatedVal = option.component_response
                const populatedBool =
                    populatedVal === true || populatedVal === 'true'

                return currentBool === populatedBool
            })
        }

        const componentIdStr = String(component.id)
        const fieldValueData = currentFieldValues[componentIdStr]

        return !!(
            !fieldValueData ||
            fieldValueData === component.component_response ||
            (typeof fieldValueData === 'object' &&
                fieldValueData?.value ===
                component.component_response?.value)
        )
    }

    const handlePopulateClose = () => {
        setPopulatedSuccess(false)
    }

    const handlePopulateErrorClose = () => {
        setPopulateError(false)
    }

    const handleFormNotSavedClose = () => {
        setFormNotSavedAlert(false)
    }

    console.log({
        ims_forms_data,
        imsForm,
        prevEvalItem,
        selectedCategory,
        fieldValues,
        newIMSData,
    })

    const formData = useMemo(() => {
        const baseData =
            ims_forms_data || newIMSData || imsForm?.item?.ims_forms_data

        if (!baseData) return null

        const savedImsData = imsForm?.item?.ims_forms_data

        // If we are using the template (ims_forms_data) but have saved data for this category,
        // merge the is_auto_populated flags.
        if (
            baseData === ims_forms_data &&
            savedImsData &&
            (savedImsData.category === baseData.category ||
                savedImsData.id === baseData.id)
        ) {
            // Deep clone to avoid mutating prop
            const mergedData = JSON.parse(JSON.stringify(baseData))

            // Merge logic
            mergedData.groups?.forEach((group: any) => {
                const savedGroup = savedImsData.groups?.find(
                    (g: any) => g.id === group.id
                )
                if (savedGroup) {
                    group.components?.forEach((component: any) => {
                        const savedComponent = savedGroup.components?.find(
                            (c: any) => c.id === component.id
                        )
                        if (savedComponent) {
                            if (savedComponent.is_auto_populated) {
                                component.is_auto_populated = true
                            }
                            // Also copy the component_response (the populated value) so we can compare against current value
                            if (savedComponent.component_response !== undefined) {
                                component.component_response =
                                    savedComponent.component_response
                            }

                            // Merge values for checkbox groups to preserve populated state of options
                            if (
                                component.values &&
                                savedComponent.values &&
                                Array.isArray(component.values) &&
                                Array.isArray(savedComponent.values)
                            ) {
                                component.values = component.values.map(
                                    (val: any) => {
                                        const savedVal =
                                            savedComponent.values.find(
                                                (sv: any) => sv.id === val.id
                                            )
                                        if (savedVal) {
                                            // If saved value has response, use it
                                            return { ...val, ...savedVal }
                                        }
                                        return val
                                    }
                                )
                            }
                        }
                    })
                }
            })
            return mergedData
        }

        return baseData
    }, [ims_forms_data, newIMSData, imsForm])
    console.log({ formData })
    const groups = formData?.groups

    if (!selectedCategory) {
        return (
            <div
                style={{
                    padding: '20px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    background: '#f9f9f9',
                    maxHeight: '95%',
                    overflowY: 'auto',
                    marginLeft: '20px',
                    marginRight: '20px',
                    marginTop: '20px',
                }}
            >
                <Snackbar
                    open={successAlert}
                    autoHideDuration={6000}
                    onClose={handleSaveSuccessClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleSaveSuccessClose}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        IMS form is saved successfully!
                    </Alert>
                </Snackbar>
                <LoadFormPreview />
            </div>
        )
    }

    if (loading || !groups) {
        return (
            <div
                style={{
                    padding: '20px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    background: '#f9f9f9',
                    maxHeight: '95%',
                    overflowY: 'auto',
                    marginLeft: '20px',
                    marginRight: '20px',
                    marginTop: '20px',
                }}
            >
                <Snackbar
                    open={populateError}
                    autoHideDuration={6000}
                    onClose={handlePopulateErrorClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handlePopulateErrorClose}
                        severity="error"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        Invalid VIN provided. Verify the VIN and try again.
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={populatedSuccess}
                    autoHideDuration={6000}
                    onClose={handlePopulateClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handlePopulateClose}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        Form Populated Successfully
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={successAlert}
                    autoHideDuration={6000}
                    onClose={handleSaveSuccessClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleSaveSuccessClose}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        IMS form is saved successfully!
                    </Alert>
                </Snackbar>
                <Dialog
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '16px',
                            maxWidth: '500px',
                            width: '100%',
                        },
                    }}
                >
                    <DialogTitle
                        style={{
                            color: '#4a287d',
                            fontWeight: 600,
                            fontSize: '1.5rem',
                            paddingBottom: '8px',
                        }}
                    >
                        Enter VIN Number
                    </DialogTitle>
                    <DialogContent style={{ paddingTop: '16px' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="VIN Number"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={vinInput}
                            onChange={(e) => setVinInput(e.target.value)}
                            sx={
                                {
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: '#4a287d',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#4a287d',
                                        },
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#4a287d',
                                    },
                                } as any
                            } // Type assertion (use only if necessary)
                        />
                    </DialogContent>
                    <DialogActions
                        style={{ padding: '16px', paddingTop: '16px' }}
                    >
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            style={{
                                color: '#4a287d',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingLeft: '24px',
                                paddingRight: '24px',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleModalSubmit}
                            variant="contained"
                            style={{
                                backgroundColor: '#4a287d',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingLeft: '24px',
                                paddingRight: '24px',
                            }}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>
                {loading ? (
                    <IMSContentLoader />
                ) : (
                    <div>Error: {error || 'Form data not available'}</div>
                )}
            </div>
        )
    }

    return (
        <div
            style={{
                padding: '20px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                background: '#f9f9f9',
                maxHeight: '95%',
                height: 'calc(100vh - 230px)',
                overflowY: 'auto',
                marginLeft: '20px',
                marginRight: '20px',
                marginTop: '20px',
            }}
        >
            <Snackbar
                open={populateError}
                autoHideDuration={6000}
                onClose={handlePopulateErrorClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handlePopulateErrorClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Invalid VIN provided. Verify the VIN and try again.
                </Alert>
            </Snackbar>
            <Snackbar
                open={formNotSavedAlert}
                autoHideDuration={6000}
                onClose={handleFormNotSavedClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleFormNotSavedClose}
                    severity="warning"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Kindly select a category and save the form before populating
                    with the vin
                </Alert>
            </Snackbar>
            <Snackbar
                open={populatedSuccess}
                autoHideDuration={6000}
                onClose={handlePopulateClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handlePopulateClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Form Populated Successfully
                </Alert>
            </Snackbar>
            <Snackbar
                open={successAlert}
                autoHideDuration={6000}
                onClose={handleSaveSuccessClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSaveSuccessClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    IMS form is saved successfully!
                </Alert>
            </Snackbar>
            <Dialog
                open={overwriteDialogOpen}
                onClose={() => setOverwriteDialogOpen(false)}
            >
                <DialogTitle style={{ color: '#4a287d', fontWeight: 600 }}>
                    Existing Data Detected
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        This form already contains data. Populating from VIN will
                        overwrite existing fields. Are you sure you want to
                        proceed?
                    </Typography>
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    <Button
                        onClick={() => setOverwriteDialogOpen(false)}
                        style={{ color: '#4a287d' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmOverwrite}
                        variant="contained"
                        style={{ backgroundColor: '#4a287d', color: "#fff" }}
                    >
                        Proceed
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                PaperProps={{
                    style: {
                        borderRadius: '12px',
                        padding: '16px',
                        maxWidth: '500px',
                        width: '100%',
                    },
                }}
            >
                <DialogTitle
                    style={{
                        color: '#4a287d',
                        fontWeight: 600,
                        fontSize: '1.5rem',
                        paddingBottom: '8px',
                    }}
                >
                    Enter VIN Number
                </DialogTitle>
                <DialogContent style={{ paddingTop: '16px' }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="VIN Number"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={vinInput}
                        onChange={(e) => setVinInput(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': { borderColor: '#4a287d' },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4a287d',
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#4a287d',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions style={{ padding: '16px', paddingTop: '16px' }}>
                    <Button
                        onClick={() => setIsModalOpen(false)}
                        style={{
                            color: '#4a287d',
                            textTransform: 'none',
                            fontWeight: 500,
                            paddingLeft: '24px',
                            paddingRight: '24px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleModalSubmit}
                        variant="contained"
                        disabled={isPopulating}
                        style={{
                            backgroundColor: '#4a287d',
                            color: "#fff",
                            textTransform: 'none',
                            fontWeight: 500,
                            paddingLeft: '24px',
                            paddingRight: '24px',
                        }}
                    >
                        {isPopulating ? (
                            <CircularProgress
                                size={24}
                                style={{ color: 'white' }}
                            />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {lightboxOpen && lightboxImage && (
                <LightboxDialog
                    open={lightboxOpen}
                    image={lightboxImage}
                    onClose={() => setLightboxOpen(false)}
                />
            )}

            <div style={{ marginBottom: '20px' }}>
                <Grid
                    container
                    spacing={3}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item xs={6}>
                        <Typography style={titleStyle}>
                            IMS Form Category
                        </Typography>
                        <h2>{formData?.category}</h2>
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <Button
                            variant="contained"
                            endIcon={
                                isPopulating ? (
                                    <CircularProgress
                                        size={20}
                                        style={{ color: 'white' }}
                                    />
                                ) : (
                                    <AutoFixHighIcon sx={{ marginLeft: 1 }} />
                                )
                            }
                            onClick={handlePopulateFormToIMS}
                            className="fourth-button"
                            disabled={isPopulating || loading}
                            style={{ height: 40, width: '80%' }}
                        >
                            <Typography
                                style={{ color: 'white' }}
                                variant="button"
                            >
                                {loading || isPopulating
                                    ? 'Processing...'
                                    : 'Populate Form'}
                            </Typography>
                        </Button>
                    </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0' }} />

                <Grid container spacing={3} alignItems="center">
                    <Grid container item xs={6} direction="column">
                        <Grid item>
                            <Typography style={titleStyle}>Fee Code</Typography>
                            <h4 style={internaUseValues}>
                                {evaluation?.evaluation?.fee_code || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>Location</Typography>
                            <h4 style={internaUseValues}>
                                {imsForm?.item?.location || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                DC Service
                            </Typography>
                            <h4 style={internaUseValues}>
                                {evaluation?.evaluation?.primary_contact ||
                                    '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                Copart Lot Number
                            </Typography>
                            <h4 style={internaUseValues}>
                                {imsForm?.lot_number || '--------'}
                            </h4>
                        </Grid>
                    </Grid>

                    <Grid container item xs={6} direction="column">
                        <Grid item>
                            <Typography style={titleStyle}>
                                Form Status
                            </Typography>
                            <div style={internaUseValues2}>
                                {imsForm?.item?.form_sub ? (
                                    <Chip
                                        color="success"
                                        icon={<CheckIcon />}
                                        label="Form Submitted to IMS"
                                        size="small"
                                        style={{ fontWeight: 'light' }}
                                    />
                                ) : (
                                    <Chip
                                        icon={<InfoIcon />}
                                        label="Form is not Submitted"
                                        size="small"
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                Opportunity
                            </Typography>
                            <h4 style={internaUseValues}>
                                {formData?.opportunity_id || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>Job Code</Typography>
                            <h4 style={internaUseValues}>
                                {imsForm?.jobcode || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                Nomination
                            </Typography>
                            <FormControl
                                style={{ position: 'relative', width: '100%' }}
                            >
                                <Select
                                    labelId="nomination-label"
                                    id="nomination-select"
                                    value={nominationValue}
                                    onChange={handleNominationChange}
                                    style={{
                                        width: '70%',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: { maxHeight: 200 },
                                        },
                                    }}
                                >
                                    {[...Array(11)].map((_, index) => (
                                        <MenuItem key={index} value={index}>
                                            {index}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            </div>

            {groups && groups.length > 0 ? (
                <IMSFormAccordion
                    groups={groups}
                    fieldValues={fieldValues}
                    handleFieldChange={handleFieldChange}
                    shouldShowAutoPopulatedIndicator={
                        shouldShowAutoPopulatedIndicator
                    }
                    setLightboxImage={setLightboxImage}
                    setLightboxOpen={setLightboxOpen}
                />
            ) : (
                <div>No form fields available for this category.</div>
            )}
        </div>
    )
}

export default ImsFormFields
